import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, KeyRound, Mail, Shield } from "lucide-react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const LOGO_URL = "/images/logo-full.png";
const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  const handleRequestCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/password-reset/request`, { email: email.trim() });
      toast.success("Se l'email è registrata, riceverai un codice di verifica.");
      setStep("verify");
    } catch (error) {
      if (error.response?.status === 429) {
        toast.error("Troppe richieste. Riprova tra qualche minuto.");
      } else {
        toast.error("Errore durante la richiesta. Riprova più tardi.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/auth/password-reset/verify-2fa`, {
        email: email.trim().toLowerCase(),
        code,
      });
      const token = res.data?.reset_token;
      if (!token) {
        throw new Error("Token non ricevuto");
      }
      toast.success("Codice verificato. Imposta la nuova password.");
      navigate(`/reset-password?token=${encodeURIComponent(token)}`);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Codice non valido o scaduto");
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/password-reset/resend-code`, { email: email.trim() });
      toast.info("Se l'email è registrata, riceverai un nuovo codice.");
    } catch (error) {
      if (error.response?.status === 429) {
        toast.error("Troppe richieste. Riprova tra qualche minuto.");
      } else {
        toast.error("Errore durante l'invio. Riprova più tardi.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fbfa] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <img src={LOGO_URL} alt="Affidya" className="mx-auto mb-4 h-12" />
          <h1 className="text-2xl font-bold text-slate-900">Recupero password</h1>
          <p className="mt-1 text-sm text-slate-500">
            {step === "email"
              ? "Inserisci la tua email per ricevere un codice di verifica"
              : "Inserisci il codice inviato via email"}
          </p>
        </div>

        <section className="rounded-[1.6rem] border border-white/80 bg-white p-6 shadow-[0_24px_70px_rgba(15,23,42,0.10)]">
          {step === "email" ? (
            <form onSubmit={handleRequestCode} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="forgot-email" className="text-sm font-medium text-slate-700">
                  Email
                </Label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                  <Input
                    id="forgot-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="nome@struttura.it"
                    className="h-11 rounded-xl pl-11"
                    required
                    autoFocus
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="h-11 w-full rounded-xl bg-emerald-600 hover:bg-emerald-700"
              >
                {loading ? "Invio in corso..." : "Invia codice di verifica"}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleVerifyCode} className="space-y-4">
              <div className="rounded-lg bg-emerald-50 border border-emerald-100 px-3 py-2 text-sm text-emerald-800">
                Codice inviato a <span className="font-medium">{email}</span>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="forgot-code" className="text-sm font-medium text-slate-700">
                  Codice di verifica
                </Label>
                <div className="relative">
                  <Shield className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                  <Input
                    id="forgot-code"
                    type="text"
                    inputMode="numeric"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="000000"
                    className="h-11 rounded-xl pl-11 text-center font-mono text-lg tracking-widest"
                    maxLength={6}
                    required
                    autoFocus
                  />
                </div>
                <p className="text-xs text-slate-500">Codice a 6 cifre, valido 10 minuti</p>
              </div>

              <Button
                type="submit"
                disabled={loading || code.length !== 6}
                className="h-11 w-full rounded-xl bg-emerald-600 hover:bg-emerald-700"
              >
                {loading ? "Verifica in corso..." : "Verifica codice"}
              </Button>

              <Button
                type="button"
                variant="outline"
                onClick={handleResendCode}
                disabled={loading}
                className="h-10 w-full rounded-xl"
              >
                Invia un nuovo codice
              </Button>

              <button
                type="button"
                onClick={() => setStep("email")}
                className="w-full text-sm text-slate-500 hover:text-slate-700"
              >
                Cambia email
              </button>
            </form>
          )}

          <div className="mt-5 flex items-start gap-2 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500">
            <KeyRound className="h-4 w-4 shrink-0 mt-0.5 text-slate-400" />
            <p>
              Per sicurezza non confermiamo se l&apos;email è registrata. Il codice viene inviato
              solo agli account esistenti e attivi.
            </p>
          </div>
        </section>

        <div className="mt-4 text-center">
          <Link
            to="/login"
            className="inline-flex items-center gap-1 text-sm text-emerald-700 hover:underline"
          >
            <ArrowLeft className="h-4 w-4" />
            Torna al login
          </Link>
        </div>
      </div>
    </div>
  );
}
